import { z } from 'zod'

import { getClientEnv } from '@/env/client'
import type { WapiUserLocation } from '@/services/wapi'

/**
 * WAPI's `ping.json` geo block, narrowed to what the signup event carries. Every
 * field optional and empty strings tolerated: ping answers `''` / `null` for an IP
 * MaxMind resolves only partially, and a country-only answer is still worth having.
 */
const PingResponseSchema = z.object({
    result: z.object({
        ipLocation: z.object({
            publicIp: z.string().optional(),
            city: z.string().optional(),
            state: z.string().optional(),
            country: z.string().optional(),
            postalCode: z.string().optional(),
            loc: z
                .object({
                    lat: z.number().nullable().optional(),
                    lng: z.number().nullable().optional(),
                })
                .passthrough()
                .optional(),
        }),
    }),
})

/**
 * The visitor's IP-derived location, read **from the browser**.
 *
 * WAPI geolocates whichever IP it sees on the request, so this only works where the
 * request *is* the visitor's — here. The server cannot do it: Cloudflare fronts
 * `api.wanderu.com` and rewrites the client-IP headers with the connecting address,
 * so a forwarded visitor IP is replaced by our own egress and the answer comes back
 * as the datacenter. Verified from a prod pod: a forged `true-client-ip` was echoed
 * back as our NAT address.
 *
 * `ping.json` needs no auth, which is why the browser can call it at all.
 *
 * Resolves `undefined` on every failure rather than throwing — geo is enrichment for
 * journey segmentation, and a signup must never be lost to it. Called at submit
 * rather than on page load: a subscription is rare, and a per-pageview request would
 * cost every visitor for the few who subscribe.
 */
export async function readVisitorLocation(): Promise<WapiUserLocation | undefined> {
    const { config, logger } = getClientEnv()

    try {
        const response = await fetch(`${config.wapi.urlRoot}ping.json`, {
            method: 'GET',
            headers: { Accept: 'application/json, text/plain, */*' },
        })

        if (!response.ok) {
            logger.withMetadata({ status: response.status }).warn('Visitor location lookup failed')
            return undefined
        }

        const parsed = PingResponseSchema.safeParse(await response.json())
        if (!parsed.success) {
            logger.warn('Ping response carried no usable ipLocation')
            return undefined
        }

        const { publicIp, city, state, country, postalCode, loc } = parsed.data.result.ipLocation

        // An answer that places the visitor nowhere is not an answer. The lookup still
        // echoes the IP it saw, and on an internal network that is a private address —
        // forwarding it would write our own infrastructure's IP onto a subscriber
        // profile as their location. A country-only answer still counts: MaxMind
        // resolves plenty of addresses no finer, and country is a real segment.
        // Emptiness is the signal, so each field is tested for content rather than for
        // presence — `??` would treat ping's `''` as an answer.
        const placed =
            [city, state, country, postalCode].some((value) => Boolean(value?.trim())) ||
            loc?.lat != null ||
            loc?.lng != null

        if (!placed) {
            return undefined
        }

        return { publicIp, city, state, country, postalCode, loc }
    } catch {
        // Offline, blocked by an extension, aborted — all the same outcome: no geo,
        // and the caller carries on with the signup.
        return undefined
    }
}
